import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import AgentModal, { AgentData } from '../components/AgentModal';
import IdeaModal from '../components/IdeaModal';
import PlatformDemo from '../components/PlatformDemo';
import WorkflowSection from '../components/WorkflowSection';
import ScrollReveal from '../components/ScrollReveal';
import { agents as staticAgents } from '../data/agents';
import { useAgents } from '../hooks/useAgents';
import { useAccessCheck } from '../hooks/useAccessCheck';
import { useSeenUpdates } from '../hooks/useSeenUpdates';
import { useOktaAuth } from '@okta/okta-react';
import { getGeorgeBase } from '../utils/georgeUrl';
import { compareVersions } from '../utils/version';
import logoGeorgeBird from '../assets/avatar.png';

// Chevron right icon
const ChevronRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="ml-1 opacity-70"
  >
    <path
      d="M6 12L10 8L6 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Plus icon for the card button
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 6V18M6 12H18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Search icon
const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Idea Bulb Icon
const BulbIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z"></path>
    <path d="M9 21h6"></path>
  </svg>
);

const TIFFANY_AGENT_IDS = ['servicing', 'retail', 'analytics', 'product', 'hr'];
const ECOSYSTEM_AGENT_IDS = ['jewel', 'maia', 'maia-legal', 'lucia'];

const LockIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const AgentCard: React.FC<{
  agent: AgentData;
  accessMap: Record<string, boolean>;
  isAuthenticated: boolean;
  hasUnseenUpdate: boolean;
  statusConfig: { bg: string; text: string; border: string; dot: string };
  onSelect: (agent: AgentData, section: 'updates' | null) => void;
}> = ({ agent, accessMap, isAuthenticated, hasUnseenUpdate, statusConfig, onSelect }) => {
  const isLocked =
    isAuthenticated && agent.liveMetadata && agent.id in accessMap && !accessMap[agent.id];

  return (
    <div
      key={agent.id}
      onClick={() => onSelect(agent, hasUnseenUpdate ? 'updates' : null)}
      className="group relative h-[220px] bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden hover:border-[#81D8D0]/50 hover:shadow-xl hover:shadow-[#81D8D0]/10 transition-all duration-300 flex flex-col cursor-pointer"
    >
      <div className="absolute top-3 right-3 z-20">
        {hasUnseenUpdate ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect(agent, 'updates');
            }}
            title={`See what's new — ${agent.status}`}
            aria-label={`New update for ${agent.title ?? agent.id}`}
            className="group/new relative -m-2 flex items-center justify-center p-2"
          >
            {/* Discreet unread dot in the brand colour; the padding keeps a
                comfortable hit area around it. */}
            <span className="relative flex h-2.5 w-2.5">
              {/* Soft halo: one faint ripple every ~3s. Skipped for users who
                  prefer reduced motion. */}
              <span
                aria-hidden
                className="absolute inset-0 rounded-full bg-[#81D8D0] motion-safe:animate-dot-halo motion-reduce:hidden"
              />
              <span className="relative block h-2.5 w-2.5 rounded-full bg-[#81D8D0] ring-2 ring-white transition-transform group-hover/new:scale-125" />
            </span>
          </button>
        ) : (
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border shadow-sm backdrop-blur-md ${statusConfig.bg} ${statusConfig.border} ${statusConfig.text}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot} animate-pulse`}></span>
            <span className="text-[10px] font-bold uppercase tracking-wide leading-none">
              {agent.status}
            </span>
          </div>
        )}
      </div>
      {isLocked && (
        <div className="absolute top-3 left-3 z-20 group/lock">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 border border-red-200 text-red-500 shadow-sm">
            <LockIcon />
          </div>
          <div className="absolute left-0 top-full mt-1.5 whitespace-nowrap bg-gray-900 text-white text-[10px] font-semibold px-2 py-1 rounded-md opacity-0 group-hover/lock:opacity-100 transition-opacity pointer-events-none shadow-lg">
            Access restricted
          </div>
        </div>
      )}
      <div className="flex-grow relative flex items-center justify-center p-4 transition-transform duration-500 group-hover:scale-105">
        {agent.icon}
      </div>
      <div className="p-4 relative z-10 bg-gradient-to-t from-gray-50 via-gray-50 to-transparent">
        <div className="flex gap-2 mb-2">
          {(agent.tags ?? []).map((tag) => (
            <span
              key={tag}
              className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-md"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-end justify-between gap-3">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-black mb-0.5">{agent.title ?? ''}</h3>
            <p className="text-xs text-gray-500 line-clamp-1">{agent.description ?? ''}</p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect(agent, hasUnseenUpdate ? 'updates' : null);
            }}
            className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-600 flex items-center justify-center hover:bg-[#81D8D0] hover:text-white hover:border-[#81D8D0] transition-all duration-200 shadow-sm group-hover:shadow-md"
            aria-label={`Learn more about ${agent.title}`}
          >
            <PlusIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

const Home: React.FC = () => {
  const [selectedAgent, setSelectedAgent] = useState<AgentData | null>(null);
  const [modalSection, setModalSection] = useState<'updates' | null>(null);
  // Version the user had already seen when the modal opened, captured BEFORE
  // markSeen runs — used by the modal to highlight the newer changelog entries.
  const [modalSeenVersion, setModalSeenVersion] = useState<string | undefined>(undefined);
  const [showIdeaModal, setShowIdeaModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { authState, oktaAuth } = useOktaAuth();
  const isAuthenticated = Boolean(authState?.isAuthenticated);
  const accessToken = isAuthenticated ? (oktaAuth.getAccessToken() ?? '') : '';
  const idToken = isAuthenticated ? (oktaAuth.getIdToken() ?? '') : '';

  const agents = useAgents(staticAgents, accessToken, idToken);
  const accessMap = useAccessCheck(accessToken, idToken, agents);
  const {
    seen,
    loaded: seenLoaded,
    markSeen,
    markSeenSilently,
  } = useSeenUpdates(accessToken, idToken);

  // First-ever visit: an agent the user has never seen shows NO badge — its whole
  // changelog is new to them, so there's nothing to single out. Silently baseline
  // those agents at their current version. Real "New" badges only appear later,
  // once the changelog advances past this baseline.
  useEffect(() => {
    if (!seenLoaded) return;
    const baseline: Record<string, string> = {};
    for (const a of agents) {
      const version = a.owners?.version;
      if (version && seen[a.id] === undefined) baseline[a.id] = version;
    }
    if (Object.keys(baseline).length > 0) markSeenSilently(baseline);
  }, [seenLoaded, agents]);

  // Opening the modal marks the agent's current version seen (clearing the badge)
  // and records the previously-seen version so the modal can highlight new entries.
  const openAgent = (agent: AgentData, section: 'updates' | null) => {
    setModalSeenVersion(seen[agent.id]);
    setSelectedAgent(agent);
    setModalSection(section);
    const version = agent.owners?.version;
    if (version) markSeen(agent.id, version);
  };

  // Keep selectedAgent in sync when live metadata arrives after modal opens
  useEffect(() => {
    if (!selectedAgent) return;
    const updated = agents.find((a) => a.id === selectedAgent.id);
    if (updated) setSelectedAgent(updated);
  }, [agents]);

  const tiffanyAgents = useMemo(
    () => agents.filter((a) => TIFFANY_AGENT_IDS.includes(a.id)),
    [agents]
  );
  const ecosystemAgents = useMemo(
    () => agents.filter((a) => ECOSYSTEM_AGENT_IDS.includes(a.id)),
    [agents]
  );

  // Filter only Tiffany agents based on search query
  const filteredAgents = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return tiffanyAgents;

    return tiffanyAgents.filter(
      (agent) =>
        (agent.title ?? '').toLowerCase().includes(query) ||
        (agent.tags ?? []).some((tag) => tag.toLowerCase().includes(query))
    );
  }, [searchQuery, tiffanyAgents]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'Indus':
        return {
          bg: 'bg-emerald-50/90',
          text: 'text-emerald-700',
          border: 'border-emerald-200',
          dot: 'bg-emerald-500',
        };
      case 'Scale':
        return {
          bg: 'bg-emerald-50/90',
          text: 'text-emerald-700',
          border: 'border-emerald-200',
          dot: 'bg-emerald-500',
        };
      case 'POC':
        return {
          bg: 'bg-amber-50/90',
          text: 'text-amber-700',
          border: 'border-amber-200',
          dot: 'bg-amber-500',
        };
      default:
        return {
          bg: 'bg-gray-50/90',
          text: 'text-gray-700',
          border: 'border-gray-200',
          dot: 'bg-gray-500',
        };
    }
  };

  return (
    <div className="flex flex-col items-center">
      {/* Hero Banner Section */}
      <section className="w-full px-4 md:px-6 pt-4 md:pt-8">
        <div className="relative w-full max-w-6xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-gray-100 via-gray-50 to-[#81D8D0]/25 border border-gray-300/60 shadow-sm">
          {/* Decorative background logo */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[120%] flex items-center justify-center pointer-events-none opacity-[0.04] select-none">
            <img
              src="logo_george.png"
              alt=""
              className="w-full h-full object-contain grayscale blur-sm"
            />
          </div>

          {/* Decorative gradient orbs */}
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#81D8D0]/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-pink-200/20 blur-3xl pointer-events-none" />

          <div className="relative z-10 px-6 md:px-12 py-10 md:py-14 text-center flex flex-col items-center">
            {/* Main Title */}
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-black to-gray-600 mb-4 px-2 pb-2 leading-[1.05] animate-fade-in-up delay-100">
              George
            </h1>

            {/* Tagline */}
            <p className="max-w-2xl text-base md:text-lg text-gray-600 leading-relaxed animate-fade-in-up delay-200 font-light mb-6">
              <span className="text-black font-medium">
                Named after George Kunz, Tiffany&apos;s first chief gemmologist.
              </span>
              <br />
              Here to find you the most accurate and thorough answers.
            </p>

            {/* Call to Action */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 animate-fade-in-up delay-300">
              <a href={`${getGeorgeBase()}/chat`} target="_blank" rel="noreferrer">
                <Button variant="tiffany" className="w-full sm:w-[260px] text-base md:text-lg">
                  <span className="flex items-center justify-center gap-2">
                    <span>Ask George</span>
                    <img src={logoGeorgeBird} alt="" className="w-5 h-5 object-contain" />
                  </span>
                </Button>
              </a>
              <Button
                variant="outline"
                className="w-full sm:w-auto text-base md:text-lg"
                onClick={() => {
                  document
                    .getElementById('agents')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <span className="flex items-center justify-center gap-2">
                  Explore Agents
                  <ChevronRight />
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* Extended description below banner */}
        <div className="max-w-3xl mx-auto px-2 mt-10 md:mt-14 text-center animate-fade-in-up delay-300">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight text-black">
            What is George?
          </h2>
        </div>
        <div className="max-w-3xl mx-auto px-2 mt-6 space-y-5 text-base md:text-lg text-gray-600 leading-relaxed font-light text-center animate-fade-in-up delay-300">
          <p>
            George, powered by a suite of expert AI Agents, is engineered to transform your business
            operations. Explore our exclusive{' '}
            <span className="text-[#81D8D0] font-medium">&quot;Agent Store&quot;</span> to discover
            each Agent&apos;s unique capabilities, rules, and workflows.
          </p>
          <p>
            Our cutting-edge platform leverages LVMH assets and is enhanced with bespoke Tiffany
            &amp; Co. features, empowering your enterprise with intelligent automation. This
            innovation unlocks unparalleled efficiency and value, disrupting conventional approaches
            and securing a distinct competitive advantage.
          </p>
          <p>Connect with your Agentic Team today to explore limitless possibilities.</p>
        </div>
      </section>

      {/* TIFFANY AGENTS SECTION */}
      <section
        id="agents"
        className="w-full max-w-7xl mx-auto px-6 pt-12 md:pt-20 pb-12 md:pb-20 scroll-mt-20"
      >
        <ScrollReveal>
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight text-black">
              Our Expert Agents
            </h2>
            <p className="text-sm font-semibold text-[#81D8D0] tracking-wide uppercase mb-4">
              Discover our {tiffanyAgents.length} expert agents
            </p>
            <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-8">
              A suite of specialized intelligent agents designed to empower every facet of our
              Maison. Built by Tiffany, for Tiffany.
            </p>

            {/* Search Input Centered */}
            <div className="relative w-full max-w-md mx-auto group text-left">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Filter by name or tag..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#81D8D0]/50 focus:border-[#81D8D0] transition-all shadow-sm hover:border-gray-300"
              />
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgents.map((agent) => {
              const version = agent.owners?.version ?? undefined;
              const hasUnseenUpdate =
                seenLoaded &&
                !!version &&
                seen[agent.id] !== undefined &&
                compareVersions(version, seen[agent.id]) > 0;
              return (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  accessMap={accessMap}
                  isAuthenticated={isAuthenticated}
                  hasUnseenUpdate={hasUnseenUpdate}
                  statusConfig={getStatusConfig(agent.status ?? '')}
                  onSelect={openAgent}
                />
              );
            })}

            {/* "Give us your ideas" Card */}
            {(!searchQuery || 'give us your ideas'.includes(searchQuery.toLowerCase())) && (
              <div
                onClick={() => setShowIdeaModal(true)}
                className="group relative h-[220px] bg-gray-50/50 rounded-2xl border-2 border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50 overflow-hidden transition-all duration-300 flex flex-col cursor-pointer items-center justify-center text-center p-6"
              >
                <div className="w-16 h-16 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 mb-4 group-hover:scale-110 group-hover:text-[#81D8D0] group-hover:border-[#81D8D0]/30 transition-all duration-300 shadow-sm">
                  <BulbIcon />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Give us your ideas</h3>
                <p className="text-xs text-gray-500 leading-relaxed max-w-[200px]">
                  Submit your agent ideas to enhance George
                </p>
                <div className="mt-4 text-xs font-semibold text-[#81D8D0] opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0">
                  Submit Idea &rarr;
                </div>
              </div>
            )}

            {filteredAgents.length === 0 &&
              searchQuery &&
              !'give us your ideas'.includes(searchQuery.toLowerCase()) && (
                <div className="col-span-full py-16 text-center text-gray-500 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                  <p className="text-lg">No agents found matching &quot;{searchQuery}&quot;</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-2 text-[#81D8D0] font-medium hover:underline"
                  >
                    Clear filter
                  </button>
                </div>
              )}
          </div>
        </ScrollReveal>
      </section>

      {/* ECOSYSTEM AGENTS SECTION */}
      <section className="w-full max-w-7xl mx-auto px-6 pb-12 md:pb-20">
        <ScrollReveal>
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight text-black">
              George ecosystem
            </h2>
            <p className="text-sm font-semibold text-[#81D8D0] tracking-wide uppercase mb-4">
              Agents built by LVMH and our partners
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ecosystemAgents.map((agent) => {
              const version = agent.owners?.version ?? undefined;
              const hasUnseenUpdate =
                seenLoaded &&
                !!version &&
                seen[agent.id] !== undefined &&
                compareVersions(version, seen[agent.id]) > 0;
              return (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  accessMap={accessMap}
                  isAuthenticated={isAuthenticated}
                  hasUnseenUpdate={hasUnseenUpdate}
                  statusConfig={getStatusConfig(agent.status ?? '')}
                  onSelect={openAgent}
                />
              );
            })}
          </div>
        </ScrollReveal>
      </section>

      {/* WORKFLOW SECTION */}
      <ScrollReveal className="w-full">
        <WorkflowSection />
      </ScrollReveal>

      {/* PLATFORM DEMO SECTION - MOVED TO END */}
      <ScrollReveal className="w-full">
        <div className="text-center mb-8 md:mb-12 px-6">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight text-black">
            Endless possibilities
          </h2>
          <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto">
            Natural conversations answering all your questions
          </p>
        </div>
        <PlatformDemo />
      </ScrollReveal>

      {/* MODAL POPUP - Agent Details */}
      {selectedAgent && (
        <AgentModal
          agent={selectedAgent}
          initialSection={modalSection}
          seenVersion={modalSeenVersion}
          onClose={() => {
            setSelectedAgent(null);
            setModalSection(null);
          }}
          isLocked={
            isAuthenticated &&
            !!selectedAgent.liveMetadata &&
            selectedAgent.id in accessMap &&
            !accessMap[selectedAgent.id]
          }
        />
      )}

      {/* MODAL POPUP - Submit Idea */}
      {showIdeaModal && (
        <IdeaModal
          accessToken={accessToken}
          idToken={idToken}
          onClose={() => setShowIdeaModal(false)}
        />
      )}
    </div>
  );
};

export default Home;
